<template>
  <div>
    <transition name="slide">
      <b-row>
        <b-col cols="12" xl="12">
          <b-card>
            <template #header>
              <div class="portals-card-header">
                <h5 class="m-0">Portais</h5>
                <b-button
                  size="sm"
                  variant="primary"
                  @click="onNewPortalClicked"
                >
                  Novo Portal
                </b-button>
              </div>
            </template>
            <b-card-body>
              <div class="filters-section">
                <b-form-group label="Pesquisar" class="search-filter">
                  <b-input-group>
                    <b-input-group-prepend>
                      <b-input-group-text>
                        <i class="fas fa-search" aria-hidden="true"></i>
                      </b-input-group-text>
                    </b-input-group-prepend>
                    <b-form-input
                      v-model="filter"
                      type="search"
                      placeholder="Digite para pesquisar"
                    ></b-form-input>
                    <b-input-group-append>
                      <b-button :disabled="!filter" @click="filter = ''"
                        >Limpar</b-button
                      >
                    </b-input-group-append>
                  </b-input-group>
                </b-form-group>
                <b-form-checkbox
                  v-model="showDeleted"
                  class="show-deleted-toggle"
                  @change="onShowDeletedChange"
                >
                  Mostrar excluídos
                </b-form-checkbox>
              </div>

              <div class="table-container">
                <portals-table
                  :items="data.docs"
                  :headers="headers"
                  :loading="loading"
                  :sort-by="sortBy"
                  :sort-desc="sortDesc"
                  @row-clicked="edit"
                  @edit="edit"
                  @toggle-active="onTogglePortalActive"
                  @delete="onDeletePortal"
                  @restore="onRestorePortal"
                  @sort-changed="handleSortChanged"
                />
              </div>

              <nav class="pagination-nav">
                <b-pagination
                  :value="data.page"
                  :total-rows="data.total"
                  :per-page="data.limit"
                  prev-text="Anterior"
                  next-text="Próximo"
                  align="center"
                  @input="handlePageChange"
                />
              </nav>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import {
  getPortalList,
  setPortalActive,
  deletePortal,
  restorePortal,
} from "../../api/portal";
import PortalsTable from "./PortalsTable.vue";

export default {
  name: "PortalList",
  components: {
    PortalsTable,
  },
  data() {
    return {
      data: {
        docs: [],
        total: 0,
        page: 1,
        pages: 0,
        limit: 15,
      },
      filter: "",
      loading: false,
      sortBy: null,
      sortDesc: false,
      showDeleted: false,
    };
  },
  computed: {
    ...mapGetters(["profile"]),
    headers() {
      const headers = [];
      headers.push(
        { key: "slug", label: "Portal", sortable: true },
        {
          key: "createdAt",
          label: "Data de Criação",
          thStyle: { width: "180px !important" },
          sortable: true,
        },
        {
          key: "updatedAt",
          label: "Data de Atualização",
          thStyle: { width: "180px !important" },
          sortable: true,
        },
        {
          key: "status",
          label: "Status",
          thStyle: { width: "110px !important" },
        },
        {
          key: "actions",
          label: "Ações",
          thStyle: { width: "180px !important" },
        }
      );
      if (
        this.profile.role === "root" ||
        this.profile.managedCompanies.length
      ) {
        headers.splice(1, 0, {
          key: "company",
          label: "Empresa",
          thStyle: { "min-width": "200px !important" },
          sortable: true,
        });
      }
      return headers;
    },
  },
  watch: {
    filter() {
      this.data.page = 1;
      this.loadData();
    },
  },
  mounted() {
    this.loadData();
  },
  methods: {
    portalLink(id) {
      return `portais/${id.toString()}`;
    },

    edit(item) {
      this.$router.push({ path: `/portais/${item._id}` });
    },

    onNewPortalClicked() {
      this.$router.push({ path: "/portais/novo" });
    },

    async loadData() {
      this.loading = true;
      try {
        const response = await getPortalList({
          page: this.data.page,
          size: this.data.limit,
          sortBy: this.sortBy,
          sortDesc: this.sortDesc,
          filter: this.filter,
          includeDeleted: this.showDeleted ? "true" : undefined,
        });
        const payload = response && response.data ? response.data : null;
        const currentPage = Number(this.data.page) || 1;
        const currentLimit = Number(this.data.limit) || 15;

        if (Array.isArray(payload)) {
          const total = payload.length;
          const pages = Math.max(1, Math.ceil(total / currentLimit));
          const safePage = Math.min(Math.max(currentPage, 1), pages);
          const start = (safePage - 1) * currentLimit;

          this.data.docs = payload.slice(start, start + currentLimit);
          this.data.total = total;
          this.data.pages = pages;
          this.data.limit = currentLimit;
          this.data.page = safePage;
          return;
        }

        const docs = payload && Array.isArray(payload.docs) ? payload.docs : [];
        const total = Number(payload && payload.total);
        const limit = Number(payload && payload.limit);
        const page = Number(payload && payload.page);

        this.data.docs = docs;
        this.data.total = Number.isFinite(total) ? total : docs.length;
        this.data.limit =
          Number.isFinite(limit) && limit > 0 ? limit : currentLimit;
        this.data.page = Number.isFinite(page) && page > 0 ? page : currentPage;
        this.data.pages =
          Number(payload && payload.pages) ||
          Math.max(1, Math.ceil(this.data.total / this.data.limit));
      } catch (error) {
        console.error("Erro ao carregar portais:", error);
      } finally {
        this.loading = false;
      }
    },

    handlePageChange(page) {
      this.data.page = page;
      this.loadData();
    },

    handleSortChanged(sortOptions) {
      this.sortBy = sortOptions.sortBy;
      this.sortDesc = sortOptions.sortDesc;
      this.loadData();
    },

    onShowDeletedChange() {
      this.data.page = 1;
      this.loadData();
    },

    async onTogglePortalActive(item) {
      const willHide = item.active !== false;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        willHide
          ? `Ocultar o portal "${item.slug}"? Ele deixará de ser servido publicamente.`
          : `Reexibir o portal "${item.slug}"?`,
        {
          title: willHide ? "Ocultar Portal" : "Exibir Portal",
          size: "md",
          buttonSize: "sm",
          okVariant: willHide ? "warning" : "success",
          okTitle: willHide ? "Sim, Ocultar" : "Sim, Exibir",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await setPortalActive(item._id, !willHide);
        this.$bvToast.toast(
          willHide ? "Portal ocultado." : "Portal reexibido.",
          {
            title: "Sucesso",
            toastClass: "custom-toast custom-toast-success",
            variant: "light",
            solid: true,
            autoHideDelay: 5000,
            noCloseButton: false,
          }
        );
        this.loadData();
      } catch (error) {
        console.error("Erro ao alterar visibilidade do portal:", error);
        this.$bvToast.toast("Erro ao alterar visibilidade do portal.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    async onDeletePortal(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Excluir o portal "${item.slug}"? Ele ficará oculto e poderá ser restaurado posteriormente.`,
        {
          title: "Excluir Portal",
          size: "md",
          buttonSize: "sm",
          okVariant: "danger",
          okTitle: "Sim, Excluir",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await deletePortal(item._id);
        this.$bvToast.toast("Portal excluído.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.loadData();
      } catch (error) {
        console.error("Erro ao excluir o portal:", error);
        this.$bvToast.toast("Erro ao excluir o portal.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },

    async onRestorePortal(item) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `Restaurar o portal "${item.slug}"?`,
        {
          title: "Restaurar Portal",
          size: "md",
          buttonSize: "sm",
          okVariant: "success",
          okTitle: "Sim, Restaurar",
          cancelTitle: "Cancelar",
          footerClass: "p-2",
          hideHeaderClose: false,
          centered: true,
        }
      );
      if (!confirmed) return;
      try {
        await restorePortal(item._id);
        this.$bvToast.toast("Portal restaurado.", {
          title: "Sucesso",
          toastClass: "custom-toast custom-toast-success",
          variant: "light",
          solid: true,
          autoHideDelay: 5000,
          noCloseButton: false,
        });
        this.loadData();
      } catch (error) {
        console.error("Erro ao restaurar o portal:", error);
        this.$bvToast.toast("Erro ao restaurar o portal.", {
          title: "Erro",
          toastClass: "custom-toast custom-toast-error",
          variant: "light",
          solid: true,
          autoHideDelay: 10000,
          noCloseButton: false,
        });
      }
    },
  },
};
</script>

<style scoped>
.portals-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.filters-section {
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  align-items: flex-end;
}

.search-filter {
  flex: 1;
}

.show-deleted-toggle {
  white-space: nowrap;
  padding-bottom: 0.5rem;
}

.filters-section .form-group {
  margin-bottom: 0;
}

.filters-section .form-control,
.filters-section .custom-select {
  border-radius: 6px;
  border: 1px solid #e9ecef;
  background-color: #fff;
  color: #495057;
  height: calc(1.5em + 1rem + 2px);
  padding: 0.5rem 1rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}

.filters-section .form-control:focus,
.filters-section .custom-select:focus {
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.2);
  outline: 0;
}

.filters-section .input-group-text {
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-right: 0;
  border-radius: 6px 0 0 6px;
}

.filters-section .form-group label {
  font-weight: 600;
  font-size: 0.8rem;
  text-transform: uppercase;
  color: #495057;
  margin-bottom: 0.5rem;
}

.table-container {
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border: 1px solid #e9ecef;
  overflow: hidden;
}

.pagination-nav {
  margin-top: 1.5rem;
}

::v-deep .page-item .page-link {
  border-radius: 6px;
  margin: 0 3px;
  border: 1px solid #e9ecef;
  color: #495057;
  transition: background-color 0.2s, color 0.2s;
}

::v-deep .page-item .page-link:hover {
  background-color: #f0f2f5;
  color: #0056b3;
}

::v-deep .page-item.active .page-link {
  background-color: #007bff;
  border-color: #007bff;
  color: #fff;
}

::v-deep .page-item.disabled .page-link {
  background-color: transparent;
  color: #adb5bd;
}

::v-deep .card {
  border-radius: 8px;
  border: 1px solid #e0e0e0;
}

::v-deep .card-header {
  border-radius: 8px 8px 0 0;
}

::v-deep .card-body {
  padding: 1.5rem;
}

/* Sidebar Styles */
.sidebar-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1040;
}

.portal-sidebar {
  position: fixed;
  top: 0;
  right: 0;
  width: 50%;
  height: 100vh;
  background-color: #fff;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.15);
  z-index: 1050;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
}

.sidebar-header h4 {
  margin: 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
}

.close-btn {
  padding: 0;
  font-size: 1.5rem;
  color: #6c757d;
  text-decoration: none;
}

.close-btn:hover {
  color: #495057;
}

.sidebar-content {
  flex: 1;
  overflow-y: auto;
  padding: 1.5rem;
}

/* Sidebar Animation */
.slide-sidebar-enter-active,
.slide-sidebar-leave-active {
  transition: transform 0.3s ease;
}

.slide-sidebar-enter,
.slide-sidebar-leave-to {
  transform: translateX(100%);
}

.slide-sidebar-enter-to,
.slide-sidebar-leave {
  transform: translateX(0);
}

/* Responsive */
@media (max-width: 992px) {
  .portal-sidebar {
    width: 75%;
  }
}

@media (max-width: 768px) {
  .portal-sidebar {
    width: 90%;
  }
}
</style>
